import {
  Modal,
  useGoogleRecaptchaV2,
  useScreenDetect,
} from '@sgcmpteltd/uikit-next';
import { Button, Input } from '@sgcmpteltd/uikit-react';
import {
  BUTTON_COLOR_RED,
  BUTTON_VARIANT_OUTLINED,
} from '@sgcmpteltd/uikit-react/components/button';
import {
  Result,
  RESULT_VARIANT_WARNING,
} from '@sgcmpteltd/uikit-react/components/result';
import cx from 'clsx';
import Image from 'next/image';
import { useContext, useRef, useState } from 'react';

import { ACTION_STATUS_ERROR, ACTION_STATUS_SUCCESS } from '@/actions';
import { postEnquiryFormStep1 } from '@/actions/new-car/enquiry-form/post';
import { TextLink } from '@/components/text-link';
import { GOOGLE_RECAPTCHA_SITE_KEY } from '@/configs/google/recaptcha';
import { UserContext } from '@/context/user';
import { joinSgcmMedia } from '@/utils/url/join/public';

import { ICON_HEIGHT, ICON_WIDTH } from './constant';
import { EnquiryFormModal } from './enquiry-form-modal/component';
import styles from './styles.module.scss';
import type { Component, OnGoogleRecaptchSuccess } from './types';

export const EnquiryForm: Component = ({
  carId,
  make,
  model,
  isSpotlight,
  promotions,
  dealers,
}) => {
  const { isIpadMini, isDesktop } = useScreenDetect();
  const isIpadMiniMode = isIpadMini();
  const isDesktopMode = isDesktop();
  const user = useContext(UserContext);
  const [userName, setUserName] = useState<string>(user?.username ?? ''); // @suppress longLineCheck
  const [contactNumber, setContactNumber] = useState<string>('');
  const [enquiryFormId, setEnquiryFormId] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('Something Went Wrong'); // @suppress longLineCheck
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isNameInputInvalid, setIsNameInputInvalid] = useState<boolean>(false);
  const [isContactInputInvalid, setIsContactInputInvalid] = useState<boolean>(false); // @suppress longLineCheck

  const googleRecaptchaWidgetElRef = useRef<HTMLDivElement | null>(null);
  const onGoogleRecaptchSuccessRef = useRef<
    OnGoogleRecaptchSuccess | null
  >(null);

  onGoogleRecaptchSuccessRef.current = async (recaptchaToken) => {
    const { ga, gtag, dataLayer } = window;

    if (!ga) {
      return;
    }

    const eventCategory = isDesktopMode ? 'Landing Page' : 'Mobile PIsms';
    const eventAction = 'click';
    const eventLabel = isDesktopMode ? 'PIsms' : 'Landing Page';
    const eventName = isDesktopMode ? 'PIsms' : 'Mobile PIsms';

    ga('send', 'event', eventCategory, eventAction, eventName);

    if (isDesktopMode) {
      if (gtag) {
        gtag('event', eventName, {
          eventCategory,
          eventAction,
          eventLabel: eventName,
        });
      }
    } else if (dataLayer) {
      dataLayer.push({ eventDetail: null });
      dataLayer.push({
        event: eventName,
        eventDetail: {
          eventAction,
          eventCategory,
          eventLabel,
        },
      });
    }
    const formData = new FormData();

    formData.append('name', userName);
    formData.append('contact', contactNumber);
    formData.append('carId', `${carId}`);
    formData.set('recaptchaToken', recaptchaToken);

    const actionResult = await postEnquiryFormStep1(
      formData,
    );

    if (actionResult.status === ACTION_STATUS_ERROR) {
      const { error } = actionResult;

      setErrorMessage(`Something went wrong: ${error}`);
      setIsErrorModalOpen(true);
    }

    if (actionResult.status === ACTION_STATUS_SUCCESS) {
      const { data } = actionResult;

      setEnquiryFormId(data.data.id);
      setIsOpen(true);
    }
  };

  const { reset, execute } = useGoogleRecaptchaV2({
    siteKey: GOOGLE_RECAPTCHA_SITE_KEY ?? '',
    widgetElRef: googleRecaptchaWidgetElRef,
    optionsRef: useRef({
      size: 'invisible',
    }),
    onSuccess: async (recaptchaToken) => {
      onGoogleRecaptchSuccessRef.current?.(recaptchaToken);
    },
    onExpired: () => {
      reset();
      execute();
    },
  });

  const handleErrorModalOpen = () => {
    const errorModalMessage = 'Please fill up all required fields.';

    setErrorMessage(errorModalMessage);
    setIsErrorModalOpen(true);
  };

  const handleSubmitEnquiryForm = () => {
    const singaporeContactNumberPattern = /^[689]\d{7}$/;

    if (userName === '') {
      setIsNameInputInvalid(true);
      handleErrorModalOpen();
      return;
    }

    if (contactNumber === '') {
      setIsContactInputInvalid(true);
      handleErrorModalOpen();
      return;
    }

    if (!singaporeContactNumberPattern.test(contactNumber)) {
      setIsContactInputInvalid(true);
      setErrorMessage('Invalid Contact Number');
      setIsErrorModalOpen(true);
      return;
    }
    execute();
  };

  return (
    <div
      className={cx(
        styles.containerRoot,
        isSpotlight ? styles.isRedBackground : '',
        styles.isBorder,
      )}
      data-tracking-selector="free-form">
      <EnquiryFormModal
        dealersCount={dealers.length}
        enquiryFormId={enquiryFormId}
        isFromMobile={!isIpadMiniMode}
        isOpen={isOpen}
        onClose={() => {
          setIsOpen(false);
        }} />
      <Modal
        className={cx(
          styles.containerRoot,
          styles.isMediumModal,
        )}
        isOpen={isErrorModalOpen}
        title=""
        onClose={() => {
          setIsErrorModalOpen(false);
        }}>
        <div className={styles.errorModal}>
          <Result
            message=""
            title={errorMessage}
            variant={RESULT_VARIANT_WARNING} />
        </div>
      </Modal>
      {
        isIpadMiniMode && (
          <Image
            alt="icon girl texting"
            className={styles.iconGirlTexting}
            height={ICON_HEIGHT}
            src={`${joinSgcmMedia('/public/new-cars/girlTexting.svg')}`}
            width={ICON_WIDTH} />
        )
      }
      <div className={cx(
        styles.containerEnquiryFormBody,
        !isIpadMiniMode ? styles.spotlightBodyPadding : '',
      )}>
        <div className={styles.enquiryFormText}>
          {
            isSpotlight && (
              <span>
                {/* eslint-disable-next-line @stylistic/max-len */}
                Attractive savings! Buy with confidence with Sgcarmart accredited verified dealer,
                {' '}
                {dealers[0].name}
                {/* eslint-disable-next-line @stylistic/max-len */}
                ! Visit out showroom today for the best deals in town! Contact me directly with more info.
              </span>
            )
          }
          {
            !isSpotlight && promotions.length > 0 && (
              <>
                <div>
                  We have some dealers currently running very good deals on the
                  {' '}
                  <span className={styles.enquiryFormTextMakeModel}>
                    {make}
                    {' '}
                    {model}
                    .
                  </span>
                </div>
                <div>
                  Get them to contact you directly with more info.
                </div>
              </>
            )
          }
          {
            !isSpotlight && promotions.length === 0 && (
              <span>
                Get the dealers selling the
                {' '}
                <span className={styles.enquiryFormTextMakeModel}>
                  {make}
                  {' '}
                  {model}
                </span>
                {' '}
                to contact me with their best deals.
              </span>
            )
          }
        </div>
        <div className={styles.containerTextInput}>
          <Input
            className={cx(
              styles.inputField,
              isNameInputInvalid && styles.inputFieldError,
              isSpotlight && !isIpadMiniMode
                ? styles.spotlightTextNameInput
                : styles.textNameInput,
            )}
            defaultValue={userName}
            placeholder="Enter Full Name*"
            onChange={({
              target: {
                value: newUserName,
              },
            }) => {
              if (newUserName.length > 0) {
                setIsNameInputInvalid(false);
              }
              setUserName(newUserName);
            }} />
          <Input
            className={cx(
              styles.inputField,
              isContactInputInvalid && styles.inputFieldError,
              isSpotlight && !isIpadMiniMode
                ? styles.spotlightTextNumberInput
                : styles.textNumberInput,
            )}
            placeholder="Contact Number*"
            value={contactNumber}
            onChange={({
              target: {
                value: newContactNumber,
              },
            }) => {
              if (newContactNumber.length > 0) {
                setIsContactInputInvalid(false);
              }

              if (newContactNumber.length <= 8) {
                setContactNumber(newContactNumber);
              }
            }} />
          {
            isIpadMiniMode && (
              <Button
                className={styles.buttonSubmit}
                color={BUTTON_COLOR_RED}
                variant={BUTTON_VARIANT_OUTLINED}
                onClick={async () => {
                  await handleSubmitEnquiryForm();
                }}>
                Submit
              </Button>
            )
          }
        </div>
        {
          !isIpadMiniMode && (
            <Button
              className={styles.buttonSubmit}
              color={BUTTON_COLOR_RED}
              variant={BUTTON_VARIANT_OUTLINED}
              onClick={async () => {
                await handleSubmitEnquiryForm();
              }}>
              Submit
            </Button>
          )
        }
        <div className={styles.containerPolicyText}>
          {/* eslint-disable-next-line @stylistic/max-len */}
          By submitting, you consent to the terms stated in our
          {' '}
          <TextLink
            className={styles.enquiryFormPolicyTextLink}
            href="https://www.sgcarmart.com/pdpa"
            target="_blank"
            text="PDPA" />
          .
          This site is protected by reCAPTCHA. Google&apos;s
          {' '}
          <TextLink
            className={styles.enquiryFormPolicyTextLink}
            href="https://policies.google.com/privacy"
            target="_blank"
            text="Privacy Policy" />
          {' '}
          and
          {' '}
          <TextLink
            className={styles.enquiryFormPolicyTextLink}
            href="https://policies.google.com/terms"
            target="_blank"
            text="T&C" />
          {' '}
          apply.
        </div>
      </div>
      <div ref={googleRecaptchaWidgetElRef} />
    </div>
  );
};
